import React, { useState } from 'react';
import { WizardProvider } from './wizardContext';
import PropTypes from 'prop-types';
import ModalCancelRefund from '../refund/modal-cancel-refund';
import ModalSpecialRefund from '../refund/modal-special-refund';
import { connect } from 'react-redux';

const Wizard = ({
  accessGranted,
  children,
  dispatch,
  errorFulfillRefund,
  errorQuoteRefund,
  confirmRefund,
  footer,
  fulfillRefundData,
  header,
  isSignIn,
  loadingFulfillRefund,
  loginRequired = false,
  quoteRefundData,
  refundCode96Dot5,
  refundLoader,
  refundNotEligible,
  refundReason,
  showStepZeroOfFlow,
}) => {
  // Variables
  const childrenCount = React.Children.count(children);

  const [step, setStep] = useState(showStepZeroOfFlow ? 0 : 1);
  const [cancelRefundModal, setCancelRefundModal] = useState();
  const [specialRefundModal, setSpecialRefundModal] = useState();

  const isLastPage = step === childrenCount - 1;

  const signInValidation = isSignIn || !loginRequired;
  const fulfillCode = fulfillRefundData?.fulfillRefundResult?.code;
  const hasRefundedItems = (fulfillRefundData?.refunds || []).some(refund => refund?.refunded === true);

  const [penaltyChargeCard, setPenaltyChargeCard] = useState();

  // this object is passed to "modal refund not applicable" through "wizardContext"
  // the modal of error paints a different button and description according to these values
  let modalRefundNotApplicableValidations = {
    description: '',
    reason: '',
    showSpecialRefundButton: false,
    showExitButton: false,
    showTaxRefundButton: false,
    showRetryButton: false,
  };
  // If there are tickets with "D" treatment, we need to paint an extra button in "refund successful" and "refund confirmation mobile view"
  // I pass this variable through "wizardContext" to those two files
  let thereAreDTickets = false;
  // This helps me show the modal of error or the current step, we validate with the following 'if else's
  let quoteValidation = false;
  let nonRefundableExtrasExist = false;
  // As the name says, in case I need to show a list of refundable tickets I push the quotations to this array
  // I pass these array to "refund breakdown" through "wizardContext"
  let quotationsToRender = [];
  let GTicketList = [];
  // I only store "processing" from the object I receive from the endpoint to "arrayOfTreatmentCodes"
  // example of endpoint: https://infosys-content-stage.pantheonsite.io/es/api/v1/errors-refund/97.3+96.5?language=es&status=1
  const arrayOfTreatmentCodes = [];

  (refundReason?.arrayOfCodes || []).forEach(item => {
    if (!arrayOfTreatmentCodes.includes(item?.processing)) {
      arrayOfTreatmentCodes.push(item?.processing);
    }
  });

  // A validation to check if there is at least 1 ticket with subcode 00 inside the pnr
  let thereAreTickets00 = false;
  if (!refundReason?.fulfillMessageFetched) {
    thereAreTickets00 = (quoteRefundData?.quotations || []).some((item) => {
      return item?.quoteItemResult?.subCode === '00';
    });
  }
  let errorObject = {};

  // Bussiness Rules, if any rule fails or changes, this is what needs to be changed
  if ((arrayOfTreatmentCodes.includes('A') || arrayOfTreatmentCodes.includes('B'))
    && (arrayOfTreatmentCodes.includes('C') || thereAreTickets00)) {
    if (arrayOfTreatmentCodes.includes('A')) {
      errorObject = (refundReason?.arrayOfCodes || []).find(item => item.processing === 'A');
    } else {
      errorObject = (refundReason?.arrayOfCodes || []).find(item => item.processing === 'B');
    }
    modalRefundNotApplicableValidations = {
      ...modalRefundNotApplicableValidations,
      description: errorObject?.description,
      reason: errorObject?.reason,
      showSpecialRefundButton: errorObject?.specialButton,
    };
  } else if (arrayOfTreatmentCodes.includes('A')) {
    // If there is at least 1 ticket with processing A, we show the modal of error, that's why "quoteValidation" remains false
    // We get the description, reason, and specialButton from the object where processing is A
    // We put the information in "modalRefundNotApplicableValidations" and send it to
    // "modal refund not applicable" through "wizardContext"
    errorObject = (refundReason?.arrayOfCodes || []).find(item => item.processing === 'A');

    modalRefundNotApplicableValidations = {
      ...modalRefundNotApplicableValidations,
      description: errorObject?.description,
      reason: errorObject?.reason,
      showSpecialRefundButton: errorObject?.specialButton,
    };
  } else if (arrayOfTreatmentCodes.includes('B')) {
    // The same with B, if there is at least 1 B ticket we do the same
    errorObject = (refundReason?.arrayOfCodes || []).find(item => item.processing === 'B');

    modalRefundNotApplicableValidations = {
      ...modalRefundNotApplicableValidations,
      description: errorObject?.description,
      reason: errorObject?.reason,
      showSpecialRefundButton: errorObject?.specialButton,
    };
  } else if (arrayOfTreatmentCodes.includes('C')) {
    // If there is at least one C ticket, we need to show a list of all tickets with subcode 00 and price different from zero
    // or tickets with processing C and price different from zero, that's why we push all of those to "quotationsToRender"
    // We pass 'quotationsToRender' to 'refund breakdown' through "wizardContext"
    // WE DON'T WANT TO SHOW THE MODAL OF ERROR, that's why "quoteValidation" is now true
    quotationsToRender = (quoteRefundData?.quotations || []).filter(quotation => {
      const quotationSubCode = quotation?.quoteItemResult?.subCode?.trim() || '';
      const errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.subcode === quotationSubCode);

      return (errorObject?.processing === 'C' && quotation?.quoteItemAmount?.dAmount !== 0)
        || (errorObject?.processing === 'D' && quotation?.quoteItemAmount?.dAmount !== 0)
        || ((quotationSubCode === '00' || quotationSubCode === '') && quotation?.quoteItemAmount?.dAmount !== 0);
    });

    GTicketList = (quoteRefundData?.quotations || []).filter(quotation => {
      const quotationSubCode = quotation?.quoteItemResult?.subCode?.trim() || '';
      const errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.subcode === quotationSubCode);
      
      return errorObject?.processing === 'G' && quotation?.quoteItemAmount?.dAmount === 0;
    });
    
    nonRefundableExtrasExist = GTicketList.length > 0;
    quoteValidation = true;
  } else if (arrayOfTreatmentCodes.includes('D')) {
    // If there is a D ticket we render tickets D with price different from zero
    // Also include tickets with subcode 00 and price different from zero
    // This case is a little special, first it will show the modal of error but if you clic "refund taxes" it will continue to...
    // step 1 and show whatever "quotationsToRender" has (D tickets + 00 tickets).
    quotationsToRender = (quoteRefundData?.quotations || []).filter(quotation => {
      const quotationSubCode = quotation?.quoteItemResult?.subCode?.trim() || '';
      const errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.subcode === quotationSubCode);
      
      return (errorObject?.processing === 'D' && quotation?.quoteItemAmount?.dAmount !== 0)
        || ((quotationSubCode === '00' || quotationSubCode === '') && quotation?.quoteItemAmount?.dAmount !== 0);
    });

    GTicketList = (quoteRefundData?.quotations || []).filter(quotation => {
      const quotationSubCode = quotation?.quoteItemResult?.subCode?.trim() || '';
      const errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.subcode === quotationSubCode);
      
      return errorObject?.processing === 'G' && quotation?.quoteItemAmount?.dAmount === 0;
    });

    nonRefundableExtrasExist = GTicketList.length > 0;
    errorObject = (refundReason?.arrayOfCodes || []).find(item => item.processing === 'D');
    modalRefundNotApplicableValidations = {
      ...modalRefundNotApplicableValidations,
      description: errorObject?.description,
      reason: errorObject?.reason,
      showTaxRefundButton: true,
      showSpecialRefundButton: errorObject?.specialButton,
    };
  } else if (arrayOfTreatmentCodes.includes('G') && !thereAreTickets00) {
    errorObject = (refundReason?.arrayOfCodes || []).find(item => item.processing === 'G');
    modalRefundNotApplicableValidations = {
      ...modalRefundNotApplicableValidations,
      description: errorObject?.description,
      reason: errorObject?.reason,
      showSpecialRefundButton: errorObject?.specialButton,
    };
  } else if (arrayOfTreatmentCodes.includes('E') && !thereAreTickets00) {
    // If there is a D ticket and THERE ARE NOT tickets with subcode 00, we show the modal of error
    // There is a priority of messages, first 97.13, then 97.3 then all others, that's why there are two if's
    errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.processing === 'E' && item?.subcode === '97.13');
    if (!errorObject) {
      errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.processing === 'E' && item?.subcode === '97.3');
      if (!errorObject) {
        errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.processing === 'E');
      }
    }

    modalRefundNotApplicableValidations = {
      ...modalRefundNotApplicableValidations,
      description: errorObject?.description,
      reason: errorObject?.reason,
      showExitButton: true,
    };
  } else if (arrayOfTreatmentCodes.includes('F') && !thereAreTickets00) {
    // If there is a D ticket and THERE ARE NOT tickets with subcode 00, we show the modal of error
    errorObject = (refundReason?.arrayOfCodes || []).find(item => item.processing === 'F');

    modalRefundNotApplicableValidations = {
      ...modalRefundNotApplicableValidations,
      description: errorObject?.description,
      reason: errorObject?.reason,
      showExitButton: true,
      showRetryButton: true,
    };
  } else {
    // Happy path, all tickets inside a pnr have subcode 00, so we render those with subcode 00 and price different from zero
    quotationsToRender = (quoteRefundData?.quotations || []).filter(quotation => {
      const quotationSubCode = quotation?.quoteItemResult?.subCode?.trim() || '';
      if (quotationSubCode === '00' && quotation?.quoteItemAmount?.dAmount !== 0) {
        return quotation;
      }
    });
    if (arrayOfTreatmentCodes.includes('D')) {
      thereAreDTickets = true;
    }

    GTicketList = (quoteRefundData?.quotations || []).filter(quotation => {
      const quotationSubCode = quotation?.quoteItemResult?.subCode?.trim() || '';
      const errorObject = (refundReason?.arrayOfCodes || []).find(item => item?.subcode === quotationSubCode);
      
      return errorObject?.processing === 'G' && quotation?.quoteItemAmount?.dAmount === 0;
    });

    nonRefundableExtrasExist = GTicketList.length > 0;
    quoteValidation = true;
  }

  let showCurrentStep = (step === 0)
    || ((quoteValidation || refundCode96Dot5) && step === 1 && signInValidation && accessGranted)
    || (fulfillCode === '00' && hasRefundedItems && step === 2 && signInValidation && accessGranted);

  // Don't show the current step if quoteRefund or fulFill api fail OR refundReasonFromCMS
  showCurrentStep = errorFulfillRefund || errorQuoteRefund || refundReason?.errorRefundReason ? false : showCurrentStep;

  // Methods
  const handleNextStep = () => {
    setStep(prev => prev + 1 > childrenCount ? step : step + 1);
  };

  const handlePrevStep = () => {
    setStep(prev => prev - 1 < 0 ? step : step - 1);
  };
  return (
    <WizardProvider
      GTicketList={ GTicketList }
      handleNextStep={ handleNextStep }
      handlePrevStep={ handlePrevStep }
      isLastPage={ isLastPage }
      modalRefundNotApplicableValidations={ modalRefundNotApplicableValidations }
      nonRefundableExtrasExist={ nonRefundableExtrasExist }
      penaltyChargeCard={ penaltyChargeCard }
      quotationsToRender={ quotationsToRender }
      setPenaltyChargeCard={ setPenaltyChargeCard }
      setCancelRefundModal={ setCancelRefundModal }
      setSpecialRefundModal={ setSpecialRefundModal }
      step={ step }
      thereAreDTickets={ thereAreDTickets }
    >
      { showCurrentStep ?
        <>
          { !loadingFulfillRefund ?
            <>
              { header ? header : null }
              <div className={ `Wizard ${step === 2 ? 'Wizard-confirmationPage' : ''}` }>
                { React.Children.map(children, (child, index) => (
                  <>
                    { step === index ? (
                      <>{ child }</>
                    ) : null }
                  </>
                )) }
              </div>
              { footer ? footer : null }
              { cancelRefundModal && <ModalCancelRefund dispatch={ dispatch } /> }
              { specialRefundModal && <ModalSpecialRefund onConfirmRefund={ confirmRefund } /> }
            </>
            : refundLoader
          }
        </>
        : refundNotEligible
      }
    </WizardProvider>
  );
};

Wizard.propTypes = {
  footer: PropTypes.object,
  header: PropTypes.object,
  startingStep: PropTypes.number,
};

const mapStateToProps = (state) => ({
  errorFulfillRefund: state.fulfillRefund.errorFulfillRefund,
  errorQuoteRefund: state.quoteRefund.errorQuoteRefund,
  fulfillRefundData: state.fulfillRefund.fulfillRefundData,
  loadingFulfillRefund: state.fulfillRefund.loadingFulfillRefund,
  loginRequired: state.Manage.stateStoreFront.loginRequired,
  quoteRefundData: state?.quoteRefund?.quoteRefundData,
  refundCode96Dot5: state.refundCode96Dot5.refundCode96Dot5,
  refundReason: state.refundReason,
});

export default connect(mapStateToProps)(Wizard);
